import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { ChevronLeft, ChevronRight, Plus, LayoutList, CalendarDays, ChevronRight as ArrowRight, Check, Search, X, ShoppingBag } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { getMonthGrid, getWeekBars } from '../utils/calendarGrid';
import { getContrastColor } from '../utils/color';

function koreaToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const PAGE_SIZE = 10;

function addDays(dateStr, days) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

// ── 목록형 뷰 ─────────────────────────────────────────────────────
function ListView({ schedules, onToggleDone, onNavigate }) {
  const today = koreaToday();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const fmt = (d) => { const [, m, day] = d.split('-'); return `${m}/${day}`; };

  const status = (s) => {
    if (s.done) return { label: '완료', color: 'text-green-500' };
    if (s.endDate < today) return { label: '지남', color: 'text-red-400' };
    if (s.startDate <= today && today <= s.endDate) return { label: '진행중', color: 'text-brand' };
    return { label: '예정', color: 'text-gray-500' };
  };

  const sorted = useMemo(() => [...schedules].sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    return a.startDate.localeCompare(b.startDate);
  }), [schedules]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? sorted.filter(s => s.name.toLowerCase().includes(q)) : sorted;
  }, [sorted, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleQuery = (v) => { setQuery(v); setPage(1); };

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-2">
      {/* 검색 */}
      <div className="relative flex-shrink-0 px-1">
        <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
        <input
          value={query}
          onChange={e => handleQuery(e.target.value)}
          placeholder="일정 검색..."
          className="w-full bg-gray-900 border border-gray-800 rounded-xl pl-9 pr-8 py-2 text-sm text-white placeholder-gray-700 focus:outline-none focus:border-gray-600"
        />
        {query && (
          <button onClick={() => handleQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-400">
            <X size={13} />
          </button>
        )}
      </div>

      {/* 건수 */}
      <div className="flex items-center justify-between px-1 flex-shrink-0">
        <p className="text-gray-600 text-xs">
          {query ? `"${query}" 검색 결과 ${filtered.length}건` : `전체 ${filtered.length}건`}
        </p>
        {totalPages > 1 && (
          <p className="text-gray-700 text-xs">{currentPage} / {totalPages}</p>
        )}
      </div>

      {/* 목록 */}
      <div className="flex-1 overflow-y-auto px-1 space-y-1.5">
        {paged.length === 0 ? (
          <p className="text-gray-600 text-sm text-center py-12">
            {query ? '검색 결과가 없습니다.' : '등록된 일정이 없습니다.'}
          </p>
        ) : paged.map(s => {
          const st = status(s);
          return (
            <div key={s.id}
              className={`flex items-center gap-3 border rounded-xl px-4 py-3 transition-colors cursor-pointer group ${
                s.done ? 'bg-gray-950 border-gray-800/50 hover:border-gray-700' : 'bg-gray-900 border-gray-800 hover:border-gray-600'
              }`}
              onClick={() => onNavigate(s.id)}
            >
              <button
                onClick={e => { e.stopPropagation(); onToggleDone(s); }}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  s.done ? 'bg-green-500 border-green-500' : 'border-gray-600 hover:border-gray-400'
                }`}
              >
                {s.done && <Check size={11} className="text-white" strokeWidth={3} />}
              </button>
              <div className="flex-shrink-0 text-center w-24">
                <p className={`text-sm font-bold tabular-nums leading-tight ${s.done ? 'text-gray-600' : 'text-white'}`}>{fmt(s.startDate)}</p>
                {s.startDate !== s.endDate && (
                  <p className={`text-xs tabular-nums leading-tight ${s.done ? 'text-gray-700' : 'text-gray-500'}`}>~ {fmt(s.endDate)}</p>
                )}
                <p className={`text-[10px] mt-0.5 ${st.color}`}>{st.label}</p>
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color, opacity: s.done ? 0.4 : 1 }} />
                <p className={`text-sm font-medium truncate ${s.done ? 'text-gray-600 line-through' : 'text-white'}`}>{s.name}</p>
                {s.hasNote && <span className="w-1.5 h-1.5 rounded-full bg-brand/60 flex-shrink-0" title="메모 있음" />}
              </div>
              <ArrowRight size={14} className="text-gray-700 group-hover:text-gray-500 flex-shrink-0 transition-colors" />
            </div>
          );
        })}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 flex-shrink-0 py-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
            className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed">
            <ChevronLeft size={15} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
            .reduce((acc, p, i, arr) => {
              if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) => p === '...' ? (
              <span key={`e${i}`} className="px-1 text-gray-700 text-xs">...</span>
            ) : (
              <button key={p} onClick={() => setPage(p)}
                className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${
                  p === currentPage ? 'bg-brand text-white' : 'text-gray-500 hover:text-white hover:bg-gray-800'
                }`}>
                {p}
              </button>
            ))
          }
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
            className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed">
            <ChevronRight size={15} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── 메인 ──────────────────────────────────────────────────────────
export default function GoodsCalendarPage({ department = 'product', basePath = '/work/product' }) {
  const today = koreaToday();
  const navigate = useNavigate();
  const [year, setYear] = useState(() => parseInt(today.split('-')[0]));
  const [month, setMonth] = useState(() => parseInt(today.split('-')[1]) - 1);
  const [view, setView] = useState('calendar');
  const [schedules, setSchedules] = useState([]);
  const [creating, setCreating] = useState(false);

  const grid = useMemo(() => getMonthGrid(year, month), [year, month]);
  const weeks = useMemo(() => {
    const r = [];
    for (let i = 0; i < grid.length; i += 7) r.push(grid.slice(i, i + 7));
    return r;
  }, [grid]);

  const loadSchedules = useCallback(() => {
    const start = grid[0].dateStr;
    const end = grid[grid.length - 1].dateStr;
    api.get('/goods-schedule', { params: { start, end, department } }).then(r => setSchedules(r.data));
  }, [grid, department]);

  const loadAll = useCallback(() => {
    api.get('/goods-schedule', { params: { department } }).then(r => setSchedules(r.data));
  }, [department]);

  useEffect(() => {
    if (view === 'list') loadAll();
    else loadSchedules();
  }, [view, loadSchedules, loadAll]);

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };
  const isThisMonth = year === parseInt(today.split('-')[0]) && month === parseInt(today.split('-')[1]) - 1;

  const weekBarsMap = useMemo(() => weeks.map(w => getWeekBars(w, schedules.map(s => ({
    ...s, title: s.name, printer: { color: s.color, name: '' },
  })), 'schedule')), [weeks, schedules]);

  // 드래그앤 드롭
  const dragRef = useRef(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);

  const handleBarDragStart = (e, rawSchedule) => {
    dragRef.current = rawSchedule;
    setDraggingId(rawSchedule.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleBarDragEnd = () => {
    setDraggingId(null);
    setDragOverDate(null);
    dragRef.current = null;
  };
  const handleCellDrop = async (e, cell) => {
    e.preventDefault();
    setDragOverDate(null);
    const schedule = dragRef.current;
    if (!schedule) return;
    dragRef.current = null;
    setDraggingId(null);
    const days = Math.round((new Date(cell.dateStr + 'T00:00:00') - new Date(schedule.startDate + 'T00:00:00')) / 86400000);
    if (days === 0) return;
    const newStart = addDays(schedule.startDate, days);
    const newEnd = addDays(schedule.endDate, days);
    setSchedules(prev => prev.map(s => s.id === schedule.id ? { ...s, startDate: newStart, endDate: newEnd } : s));
    try {
      await api.put(`/goods-schedule/${schedule.id}`, { startDate: newStart, endDate: newEnd });
    } catch {
      loadSchedules();
    }
  };

  // 새 일정 생성 후 상세 페이지로 이동
  const handleCreate = async (startDate = today) => {
    if (creating) return;
    setCreating(true);
    try {
      const r = await api.post('/goods-schedule', {
        name: '새 일정',
        color: '#818cf8',
        startDate,
        endDate: startDate,
        department,
      });
      navigate(`${basePath}/goods/${r.data.id}?from=${basePath}/goods-calendar`);
    } finally {
      setCreating(false);
    }
  };

  const handleToggleDone = async (s) => {
    await api.put(`/goods-schedule/${s.id}`, { done: !s.done });
    if (view === 'list') loadAll(); else loadSchedules();
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center gap-1">
          <ShoppingBag size={20} className="text-brand flex-shrink-0 mr-1" />
          {view === 'calendar' ? (
            <>
              <button onClick={prevMonth} className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-800 transition-colors"><ChevronLeft size={20} /></button>
              <h1 className="text-xl font-bold text-white min-w-[140px] text-center">{year}년 {month + 1}월</h1>
              <button onClick={nextMonth} className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-800 transition-colors"><ChevronRight size={20} /></button>
              {isThisMonth ? (
                <span className="px-1.5 py-0.5 text-[10px] text-brand bg-brand/10 rounded font-medium ml-1">이번 달</span>
              ) : (
                <button onClick={() => { const t = today.split('-'); setYear(+t[0]); setMonth(+t[1] - 1); }}
                  className="px-2 py-1 text-xs text-brand border border-brand/30 rounded hover:bg-brand/10 ml-1">이번 달로</button>
              )}
            </>
          ) : (
            <h1 className="text-xl font-bold text-white">전체 일정</h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleCreate()}
            disabled={creating}
            className="flex items-center gap-1 px-3 py-1.5 bg-brand text-white text-xs rounded-lg hover:bg-brand/80 disabled:opacity-50"
          >
            <Plus size={13} /> {creating ? '생성 중...' : '일정 추가'}
          </button>
          <div className="flex border border-gray-800 rounded-lg overflow-hidden">
            <button onClick={() => setView('calendar')}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs transition-colors ${view === 'calendar' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-300'}`}>
              <CalendarDays size={13} /><span className="hidden sm:inline">캘린더</span>
            </button>
            <button onClick={() => setView('list')}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs transition-colors ${view === 'list' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-300'}`}>
              <LayoutList size={13} /><span className="hidden sm:inline">목록</span>
            </button>
          </div>
        </div>
      </div>

      {/* 목록형 */}
      {view === 'list' && (
        <ListView
          schedules={schedules}
          onToggleDone={handleToggleDone}
          onNavigate={(id) => navigate(`${basePath}/goods/${id}?from=${basePath}/goods-calendar`)}
        />
      )}

      {/* 캘린더형 */}
      {view === 'calendar' && (
        <>
          <div className="grid grid-cols-7 border-b border-gray-800 flex-shrink-0">
            {WEEKDAYS.map((d, i) => (
              <div key={d} className={`text-center py-2.5 text-sm font-semibold ${i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-500'}`}>{d}</div>
            ))}
          </div>

          {(() => {
            const frVals = weeks.map((_, wi) => {
              const maxRow = weekBarsMap[wi].reduce((m, b) => Math.max(m, b.row), -1) + 1;
              return Math.max(80, 44 + maxRow * 26);
            });
            return (
              <div className="flex-1 min-h-0" style={{ display: 'grid', gridTemplateRows: frVals.map(v => `minmax(0,${v}fr)`).join(' ') }}>
                {weeks.map((weekCells, wi) => {
                  const bars = weekBarsMap[wi];
                  const maxRow = bars.reduce((m, b) => Math.max(m, b.row), -1) + 1;
                  return (
                    <div key={wi} className="border-b border-gray-800/50 relative overflow-hidden">
                      <div className="absolute inset-0 grid grid-cols-7">
                        {weekCells.map((cell, di) => (
                          <div key={di}
                            className={`${di < 6 ? 'border-r border-gray-800/30' : ''} ${cell.isCurrentMonth ? 'hover:bg-gray-800/10 cursor-pointer group' : 'bg-gray-950/30'} ${dragOverDate === cell.dateStr ? 'bg-brand/20' : ''}`}
                            onDoubleClick={() => cell.isCurrentMonth && handleCreate(cell.dateStr)}
                            onDragOver={e => { e.preventDefault(); setDragOverDate(cell.dateStr); }}
                            onDrop={e => handleCellDrop(e, cell)}
                          >
                            {cell.isCurrentMonth && (
                              <div className="flex justify-end p-0.5">
                                <button onClick={e => { e.stopPropagation(); handleCreate(cell.dateStr); }}
                                  className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-600 hover:text-brand rounded transition-opacity">
                                  <Plus size={12} />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="relative z-10 grid grid-cols-7 pointer-events-none">
                        {weekCells.map((cell, di) => {
                          const isToday = cell.dateStr === today;
                          return (
                            <div key={di} className="px-1.5 pt-1 pb-0.5">
                              <span className={`text-sm font-semibold inline-flex items-center justify-center w-7 h-7 rounded-full ${
                                isToday ? 'bg-brand text-white'
                                  : !cell.isCurrentMonth ? 'text-gray-700'
                                  : di === 0 ? 'text-red-400'
                                  : di === 6 ? 'text-blue-400'
                                  : 'text-gray-300'
                              }`}>{cell.day}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="relative z-10 mt-0.5">
                        {Array.from({ length: maxRow }, (_, ri) => (
                          <div key={ri} className="grid grid-cols-7" style={{ height: '26px' }}>
                            {bars.filter(b => b.row === ri).map(b => {
                              const color = b.schedule.printer?.color || '#818cf8';
                              const rawSchedule = schedules.find(s => s.id === b.schedule.id);
                              const done = rawSchedule?.done;
                              return (
                                <div key={b.schedule.id}
                                  draggable
                                  onDragStart={e => rawSchedule && handleBarDragStart(e, rawSchedule)}
                                  onDragEnd={handleBarDragEnd}
                                  className={`h-[23px] mx-[1px] flex items-center text-[11px] font-medium truncate cursor-grab active:cursor-grabbing hover:brightness-125 transition-all select-none ${b.isStart && b.isEnd ? 'rounded-md' : b.isStart ? 'rounded-l-md' : b.isEnd ? 'rounded-r-md' : ''} ${done ? 'opacity-35' : ''} ${draggingId === b.schedule.id ? 'opacity-40' : ''}`}
                                  style={{ gridColumn: `${b.startCol + 1} / span ${b.span}`, backgroundColor: color, color: getContrastColor(color) }}
                                  onClick={() => rawSchedule && navigate(`${basePath}/goods/${rawSchedule.id}?from=${basePath}/goods-calendar`)}
                                >
                                  <span className={`ml-1.5 truncate flex-1 ${done ? 'line-through' : ''}`}>{b.schedule.title}</span>
                                  {rawSchedule?.hasNote && (
                                    <span className="flex-shrink-0 mr-1 px-1 rounded text-[9px] font-bold leading-[14px] bg-white/25 tracking-tight">메모</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
